import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { adminApi, type AdminStats, type AdminQuery, type User, type Event, type Transaction, type Notification, type SystemConfig } from '@/services/api/admin'

export const useAdminStore = defineStore('admin', () => {
  // ==================== STATE ====================
  const dashboardStats = ref<AdminStats | null>(null)
  const recentActivities = ref<any[]>([])
  const users = ref<User[]>([])
  const events = ref<Event[]>([])
  const transactions = ref<Transaction[]>([])
  const notifications = ref<Notification[]>([])
  const gifts = ref<any[]>([])
  const systemConfig = ref<SystemConfig | null>(null)
  
  // Loading states
  const isLoading = ref(false)
  const isDashboardLoading = ref(false)
  const isUsersLoading = ref(false)
  const isEventsLoading = ref(false)
  const isTransactionsLoading = ref(false)
  const isNotificationsLoading = ref(false)
  const isGiftsLoading = ref(false)

  // Pagination
  const usersPagination = ref({ page: 1, limit: 10, total: 0, pages: 0 })
  const eventsPagination = ref({ page: 1, limit: 10, total: 0, pages: 0 })
  const transactionsPagination = ref({ page: 1, limit: 10, total: 0, pages: 0 })
  const notificationsPagination = ref({ page: 1, limit: 10, total: 0, pages: 0 })
  const giftsPagination = ref({ page: 1, limit: 10, total: 0, pages: 0 })

  // ==================== COMPUTED ====================
  const totalUsers = computed(() => dashboardStats.value?.overview?.totalUsers || 0)
  const totalEvents = computed(() => dashboardStats.value?.overview?.totalEvents || 0)
  const totalTransactions = computed(() => dashboardStats.value?.overview?.totalTransactions || 0)
  const pendingEvents = computed(() => dashboardStats.value?.overview?.pendingEvents || 0)
  const activeUsers = computed(() => dashboardStats.value?.overview?.activeUsers || 0)
  const totalRevenue = computed(() => dashboardStats.value?.overview?.totalRevenue || 0)

  // ==================== ACTIONS ====================
  
  // Dashboard
  const fetchDashboardStats = async () => {
    try {
      isDashboardLoading.value = true
      const stats = await adminApi.getDashboardStats()
      dashboardStats.value = stats
      return stats
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error)
      throw error
    } finally {
      isDashboardLoading.value = false
    }
  }

  const fetchRecentActivities = async (limit: number = 10) => {
    try {
      const activities = await adminApi.getRecentActivities(limit)
      recentActivities.value = activities
      return activities
    } catch (error) {
      console.error('Failed to fetch recent activities:', error)
      throw error
    }
  }

  // Users Management
  const fetchUsers = async (query: AdminQuery = {}) => {
    try {
      isUsersLoading.value = true
      const response = await adminApi.getUsers(query)
      users.value = response.users || []
      usersPagination.value = response.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
    } catch (error) {
      console.error('Failed to fetch users:', error)
      users.value = []
      usersPagination.value = { page: 1, limit: 10, total: 0, pages: 0 }
      throw error
    } finally {
      isUsersLoading.value = false
    }
  }

  const fetchUser = async (id: string) => {
    try {
      const user = await adminApi.getUser(id)
      return user
    } catch (error) {
      console.error('Failed to fetch user:', error)
      throw error
    }
  }

  const updateUserStatus = async (id: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED') => {
    try {
      const user = await adminApi.updateUserStatus(id, status)
      // Update user in local state
      const index = users.value.findIndex(u => u.id === id)
      if (index !== -1) {
        users.value[index] = user
      }
      return user
    } catch (error) {
      console.error('Failed to update user status:', error)
      throw error
    }
  }

  const updateUserRole = async (id: string, role: string) => {
    try {
      const user = await adminApi.updateUserRole(id, role as 'ADMIN' | 'ORGANIZER' | 'USER')
      // Update user in local state
      const index = users.value.findIndex(u => u.id === id)
      if (index !== -1) {
        users.value[index] = user
      }
      return user
    } catch (error) {
      console.error('Failed to update user role:', error)
      throw error
    }
  }

  // Admin Wallet Adjustments
  const adminDebitWallet = async (payload: { userId: string; amount: number; reason?: string }) => {
    try {
      const res = await adminApi.adminDebitWallet(payload)
      return res
    } catch (error) {
      console.error('Failed to admin debit wallet:', error)
      throw error
    }
  }

  const adminCreditWallet = async (payload: { userId: string; amount: number; reason?: string }) => {
    try {
      const res = await adminApi.adminCreditWallet(payload)
      return res
    } catch (error) {
      console.error('Failed to admin credit wallet:', error)
      throw error
    }
  }

  // Events Management
  const fetchEvents = async (query: AdminQuery = {}) => {
    try {
      isEventsLoading.value = true
      const { events: evts, pagination } = await adminApi.getEvents(query)
      events.value = evts || []
      eventsPagination.value = pagination || { page: 1, limit: 10, total: 0, pages: 0 }
    } catch (error) {
      console.error('Failed to fetch events:', error)
      events.value = []
      eventsPagination.value = { page: 1, limit: 10, total: 0, pages: 0 }
      throw error
    } finally {
      isEventsLoading.value = false
    }
  }

  const fetchEvent = async (id: string) => {
    try {
      const event = await adminApi.getEvent(id)
      return event
    } catch (error) {
      console.error('Failed to fetch event:', error)
      throw error
    }
  }

  const updateEventStatus = async (id: string, status: 'DRAFT' | 'PENDING' | 'PUBLISHED' | 'CANCELLED') => {
    try {
      const event = await adminApi.updateEventStatus(id, status)
      // Update event in local state
      const index = events.value.findIndex(e => e.id === id)
      if (index !== -1) {
        events.value[index] = event
      }
      return event
    } catch (error) {
      console.error('Failed to update event status:', error)
      throw error
    }
  }

  const approveEvent = async (id: string) => {
    try {
      const event = await adminApi.approveEvent(id)
      // Update event in local state
      const index = events.value.findIndex(e => e.id === id)
      if (index !== -1) {
        events.value[index] = event
      }
      return event
    } catch (error) {
      console.error('Failed to approve event:', error)
      throw error
    }
  }

  const rejectEvent = async (id: string, reason: string) => {
    try {
      const event = await adminApi.rejectEvent(id, reason)
      // Update event in local state
      const index = events.value.findIndex(e => e.id === id)
      if (index !== -1) {
        events.value[index] = event
      }
      return event
    } catch (error) {
      console.error('Failed to reject event:', error)
      throw error
    }
  }

  const updateEvent = async (id: string, payload: Record<string, unknown>) => {
    return adminApi.updateEvent(id, payload)
  }

  const deleteEvent = async (id: string) => {
    return adminApi.deleteEvent(id)
  }

  // Transactions Management
  const fetchTransactions = async (query: AdminQuery = {}) => {
    try {
      isTransactionsLoading.value = true
      const { transactions: txs, pagination } = await adminApi.getTransactions(query)
      transactions.value = txs || []
      transactionsPagination.value = pagination || { page: 1, limit: 10, total: 0, pages: 0 }
    } catch (error) {
      console.error('Failed to fetch transactions:', error)
      transactions.value = []
      transactionsPagination.value = { page: 1, limit: 10, total: 0, pages: 0 }
      throw error
    } finally {
      isTransactionsLoading.value = false
    }
  }

  const fetchTransaction = async (id: string) => {
    try {
      const transaction = await adminApi.getTransaction(id)
      return transaction
    } catch (error) {
      console.error('Failed to fetch transaction:', error)
      throw error
    }
  }

  const updateTransactionStatus = async (id: string, status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED') => {
    try {
      const transaction = await adminApi.updateTransactionStatus(id, status)
      // Update transaction in local state
      const index = transactions.value.findIndex(t => t.id === id)
      if (index !== -1) {
        transactions.value[index] = transaction
      }
      return transaction
    } catch (error) {
      console.error('Failed to update transaction status:', error)
      throw error
    }
  }

  // System Configuration
  const fetchSystemConfig = async () => {
    try {
      const raw = await adminApi.getSystemConfig()
      // Unwrap BE envelope { success, data } if present
      const config = (raw && raw.success === true && raw.data !== undefined) ? raw.data : raw
      systemConfig.value = config
    } catch (error) {
      console.error('Failed to fetch system config:', error)
      throw error
    }
  }

  const updateSystemConfig = async (config: Partial<SystemConfig>) => {
    try {
      const raw = await adminApi.updateSystemConfig(config)
      const updatedConfig = (raw && raw.success === true && raw.data !== undefined) ? raw.data : raw
      systemConfig.value = updatedConfig
      return updatedConfig
    } catch (error) {
      console.error('Failed to update system config:', error)
      throw error
    }
  }

  // Notifications
  const fetchNotifications = async (query: AdminQuery = {}) => {
    try {
      isNotificationsLoading.value = true
      const { notifications: notifs, pagination } = await adminApi.getNotifications(query)
      notifications.value = notifs || []
      notificationsPagination.value = pagination || { page: 1, limit: 10, total: 0, pages: 0 }
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      throw error
    } finally {
      isNotificationsLoading.value = false
    }
  }

  const createNotification = async (notification: Partial<Notification>) => {
    try {
      const newNotification = await adminApi.createNotification(notification)
      notifications.value.unshift(newNotification)
      return newNotification
    } catch (error) {
      console.error('Failed to create notification:', error)
      throw error
    }
  }

  const updateNotification = async (id: string, notification: Partial<Notification>) => {
    try {
      const updatedNotification = await adminApi.updateNotification(id, notification)
      // Update notification in local state
      const index = notifications.value.findIndex(n => n.id === id)
      if (index !== -1) {
        notifications.value[index] = updatedNotification
      }
      return updatedNotification
    } catch (error) {
      console.error('Failed to update notification:', error)
      throw error
    }
  }

  const deleteNotification = async (id: string) => {
    try {
      await adminApi.deleteNotification(id)
      // Remove notification from local state
      const index = notifications.value.findIndex(n => n.id === id)
      if (index !== -1) {
        notifications.value.splice(index, 1)
      }
    } catch (error) {
      console.error('Failed to delete notification:', error)
      throw error
    }
  }

  // ==================== GIFTS MANAGEMENT ====================
  const fetchGifts = async (query: AdminQuery = {}) => {
    try {
      isGiftsLoading.value = true
      const { gifts: gfts, pagination } = await adminApi.getGifts(query)
      gifts.value = gfts || []
      giftsPagination.value = pagination || { page: 1, limit: 10, total: 0, pages: 0 }
    } catch (error) {
      console.error('Failed to fetch gifts:', error)
      gifts.value = []
      giftsPagination.value = { page: 1, limit: 10, total: 0, pages: 0 }
      throw error
    } finally {
      isGiftsLoading.value = false
    }
  }

  const createGift = async (giftData: any) => {
    try {
      const response = await adminApi.createGift(giftData)
      await fetchGifts() // Reload gifts
      return response
    } catch (error) {
      console.error('Failed to create gift:', error)
      throw error
    }
  }

  const updateGift = async (giftId: string, giftData: any) => {
    try {
      const response = await adminApi.updateGift(giftId, giftData)
      await fetchGifts() // Reload gifts
      return response
    } catch (error) {
      console.error('Failed to update gift:', error)
      throw error
    }
  }

  const deleteGift = async (giftId: string) => {
    try {
      const response = await adminApi.deleteGift(giftId)
      await fetchGifts() // Reload gifts
      return response
    } catch (error) {
      console.error('Failed to delete gift:', error)
      throw error
    }
  }

  const toggleGiftStatus = async (giftId: string, isActive: boolean) => {
    try {
      const response = await adminApi.updateGift(giftId, { isActive })
      await fetchGifts() // Reload gifts
      return response
    } catch (error) {
      console.error('Failed to toggle gift status:', error)
      throw error
    }
  }

  // Reports
  const fetchEventsReport = async (query: AdminQuery = {}) => {
    try {
      const response = await adminApi.getEventsReport(query)
      return response
    } catch (error) {
      console.error('Failed to fetch events report:', error)
      throw error
    }
  }

  const fetchTransactionsReport = async (query: AdminQuery = {}) => {
    try {
      const response = await adminApi.getTransactionsReport(query)
      return response
    } catch (error) {
      console.error('Failed to fetch transactions report:', error)
      throw error
    }
  }

  const fetchUsersReport = async (query: AdminQuery = {}) => {
    try {
      const response = await adminApi.getUsersReport(query)
      return response
    } catch (error) {
      console.error('Failed to fetch users report:', error)
      throw error
    }
  }

  // ==================== RESET ====================
  const reset = () => {
    dashboardStats.value = null
    recentActivities.value = []
    users.value = []
    events.value = []
    transactions.value = []
    notifications.value = []
    gifts.value = []
    systemConfig.value = null
    isLoading.value = false
    isDashboardLoading.value = false
    isUsersLoading.value = false
    isEventsLoading.value = false
    isTransactionsLoading.value = false
    isNotificationsLoading.value = false
    isGiftsLoading.value = false
  }

  return {
    // State
    dashboardStats,
    recentActivities,
    users,
    events,
    transactions,
    notifications,
    gifts,
    systemConfig,
    isLoading,
    isDashboardLoading,
    isUsersLoading,
    isEventsLoading,
    isTransactionsLoading,
    isNotificationsLoading,
    isGiftsLoading,
    usersPagination,
    eventsPagination,
    transactionsPagination,
    notificationsPagination,
    giftsPagination,

    // Computed
    totalUsers,
    totalEvents,
    totalTransactions,
    pendingEvents,
    activeUsers,
    totalRevenue,

    // Actions
    fetchDashboardStats,
    fetchRecentActivities,
    fetchUsers,
    fetchUser,
    updateUserStatus,
    updateUserRole,
    fetchEvents,
    fetchEvent,
    updateEventStatus,
    approveEvent,
    rejectEvent,
    updateEvent,
    deleteEvent,
    fetchTransactions,
    fetchTransaction,
    updateTransactionStatus,
    fetchSystemConfig,
    updateSystemConfig,
    fetchNotifications,
    createNotification,
    updateNotification,
    deleteNotification,
    // Admin wallet
    adminDebitWallet,
    adminCreditWallet,
    fetchGifts,
    createGift,
    updateGift,
    deleteGift,
    toggleGiftStatus,
    fetchEventsReport,
    fetchTransactionsReport,
    fetchUsersReport,
    reset,
  }
})
